import React, {useMemo, useEffect} from 'react';
import Head from 'next/head';
import {useRouter} from 'next/router';
import _ from 'lodash';
import {
  ChatAlt2Icon,
  UsersIcon,
  AcademicCapIcon,
} from '@heroicons/react/outline';

import CoachCard from '../components/CoachCard';
import CourseCard from '../components/CourseCard';
import DocumentHeader from '../components/DocumentHeader';
import Button, {ButtonTypes} from '../components/ButtonNew';
import CoachingFilters from './CoachingPage/CoachingFilters';
import {FAQPage} from '../components/LinkedData';

import {startsWithVowel} from '../util';
import {getCanonicalURL} from '../util/seo';
import {SkillPageCopy} from '../../../lib/constants/coaching';
import {airtableRoleToRoleMap} from '../../../lib/constants/airtableTags';
import {useAnalytics} from '../hooks';

const SkillsLandingPage = ({
  coaches, tag, skill, tagMap, course, coachingTags,
}) => {
  const router = useRouter();

  // `skill` (Contentful FAQ entry) is typically null for role tags
  const faq = skill?.faq || [];
  const slug = skill?.slug || tag.id;
  const isRole = tag.type === 'role';
  const skillName = tag.name;
  const {tracker} = useAnalytics();

  const copy = SkillPageCopy[slug] || {};
  const skillNameHasInterview = skillName.toLowerCase().includes('interview');

  const pageTitle = copy.title || (skillNameHasInterview
    ? `${skillName} Coaching & Mentorship`
    : `${skillName} Interview Coaching & Mentorship`);
  const pageSubtitle = copy.subtitle || `Level up with 1:1 help from expert ${skillName} coaches and mentors from FAANG+ tech companies`;
  const metaDescription = copy.description || `Book 1:1 ${skillName} coaching: mock interviews, career guidance, and interview prep with expert ${skillName} coaches from top tech companies.`;
  const promoHeading = copy.promoHeading || (skillNameHasInterview
    ? `Prep for ${skillName.toLowerCase()} on your own time`
    : `Prep for ${skillName} interviews on your own time`);
  const promoSubheading = copy.promoSubheading || 'Study with our self-paced course, then book a coach to practice live.';

  // Filtered during render so coach cards reach the SSR HTML; coach roles are
  // Airtable names needing mapping, skills/industries are already tag ids
  const filteredCoaches = useMemo(
    () => _.filter(coaches || [], (coach) => {
      if (isRole) return _.some(coach?.Role, (r) => airtableRoleToRoleMap[r] === tag.id);
      if (tag.type === 'industry') return coach?.Industries?.includes(tag.id);
      return coach?.Skills?.includes(tag.id);
    }),
    [coaches, tag.id, tag.type, isRole],
  );

  useEffect(() => {
    tracker.skillLandingView({skill: skillName});
  }, []);

  const onClickCoach = (coach) => (e) => {
    e.stopPropagation();
    tracker.skillClickCoach({skill: skillName, coach: coach.slug});
    router.push(`/coach/${coach.slug}`);
  };

  // Filter picks hand off to /coaching with this page's tag preserved
  const onFilter = (param, value) => {
    const query = {[tag.type]: tag.id};
    if (!value) {
      delete query[param];
    } else {
      query[param] = value;
    }
    router.push({pathname: '/coaching', query});
  };

  return (
    <div className="relative bg-white overflow-hidden">
      <DocumentHeader
        title={pageTitle}
        metaDescription={metaDescription}
        socialDescription={metaDescription}
        prerender404={filteredCoaches.length === 0}
      />
      {/* Rendered directly (not via DocumentHeader's canonicalUrl prop, which
          waits for router.isReady) so crawlers see it in the static HTML */}
      <Head>
        <link rel="canonical" href={getCanonicalURL(`/coaching/${slug}`)} key="canonical" />
      </Head>
      {faq?.length > 0 && <FAQPage items={faq} />}
      <div className="pt-6 pb-8 md:pt-12 md:pb-12 flex flex-col md:flex-row justify-center items-center text-center">
        <div className="w-full max-w-4xl p-4">
          {/* Title elements */}
          <span className="inline-flex mb-2 uppercase tracking-wider text-indigo-600 mx-auto font-semibold">Coaching</span>
          <h1 className="text-2xl md:text-5xl text-center text-black font-black leading-none">
            {pageTitle}
          </h1>
          <p className="mx-auto mt-3 mb-6 text-base md:text-xl text-center text-gray-500 font-medium leading-tight max-w-xl">
            {pageSubtitle}
          </p>
        </div>
      </div>

      {/* Services description */}
      <div className="flex flex-col md:flex-row w-full max-w-6xl mx-auto mb-4">
        <div className="p-4 md:w-1/3">
          <div className="flex flex-col justify-center">
            <div className="flex items-center justify-start">
              <ChatAlt2Icon className="w-5 h-5 mr-2 text-indigo" />
              <h3 className="text-base font-semibold">{`${skillName} interview prep`}</h3>
            </div>
            <p className="mt-2 text-gray-500 font-medium">
              {`Find ${startsWithVowel(skillName) ? 'an' : 'a'} ${skillName} mentor who can help with mock interviews, applying to jobs, and more.`}
            </p>
          </div>
        </div>
        <div className="p-4 md:w-1/3">
          <div className="flex flex-col justify-center">
            <div className="flex items-center justify-start">
              <UsersIcon className="w-5 h-5 mr-2 text-indigo" />
              <h3 className="text-base font-semibold">Career coaching</h3>
            </div>
            <p className="mt-2 text-gray-500 font-medium">
              {`Find a coach with ${skillName} expertise who can help you navigate career options, switch jobs/industries, and more.`}
            </p>
          </div>
        </div>
        <div className="p-4 md:w-1/3">
          <div className="flex flex-col justify-center">
            <div className="flex items-center justify-start">
              <AcademicCapIcon className="w-5 h-5 mr-2 text-indigo" />
              <h3 className="text-base font-semibold">Mentorship</h3>
            </div>
            <p className="mt-2 text-gray-500 font-medium">
              {isRole
                ? `Level up your career with an experienced ${skillName} mentor by your side`
                : `Level up your ${skillName} with a coach who can teach you more about ${skillName}`}
            </p>
          </div>
        </div>
      </div>

      {coachingTags && (
        <div className="flex flex-col w-full max-w-6xl mx-auto px-6">
          <CoachingFilters
            role={isRole ? tag.id : undefined}
            skill={tag.type === 'skill' ? tag.id : undefined}
            industry={tag.type === 'industry' ? tag.id : undefined}
            onChange={onFilter}
            tags={coachingTags}
            basePath={`/coaching?${tag.type}=${tag.id}`}
          />
        </div>
      )}

      {/* Display top coaches */}
      <div className="flex flex-col w-full max-w-6xl mx-auto p-4">
        <div className="mb-5">
          <h2 className="text-lg md:text-2xl text-center md:text-left text-black font-black leading-none">
            {`Top ${skillName} coaches`}
          </h2>
        </div>
        {filteredCoaches.length > 0 ? (
          <>
            <div className="flex flex-col md:flex-row justify-center gap-6">
              {filteredCoaches.slice(0, 3).map((coach) => (
                <div key={coach.slug} className="w-full md:w-1/3 flex">
                  <CoachCard
                    name={coach.Name}
                    title={coach.Title}
                    image={coach.Photo}
                    href={`/coach/${coach.slug}`}
                    companies={coach.Companies}
                    skills={coach.Skills}
                    services={coach.Services}
                    rating={coach.Rating}
                    description={coach['Brief Bio']}
                    numSessions={coach['Total number of sessions completed']}
                    onClick={onClickCoach(coach)}
                    tagMap={tagMap}
                  />
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="h-56 border-4 border-gray-100 border-dashed rounded-lg flex items-center justify-center">
            <p className="text-base text-gray-500 font-semibold">
              Sorry! No coaches available at this time.
            </p>
          </div>
        )}
        <div className="flex items-center justify-center mt-5">
          {filteredCoaches.length > 3 ? (
            <Button
              buttonType={ButtonTypes.TERTIARY}
              href={`/coaching?${tag.type}=${tag.id}&src=tag-landing`}
              className="font-semibold"
              onClick={() => tracker.skillClickMoreCoach({skill: skillName})}
            >
              {`Browse more ${skillName} coaches →`}
            </Button>
          ) : (
            <Button
              buttonType={ButtonTypes.TERTIARY}
              href="/coaching"
              className="font-semibold"
              onClick={() => tracker.skillClickMoreCoach({skill: skillName})}
            >
              Browse all coaches →
            </Button>
          )}
        </div>
      </div>

      {/* Related course */}
      {course && (
        <div className="flex flex-col w-full max-w-6xl mx-auto p-4 mb-4">
          <div className="mb-5">
            <h2 className="text-lg md:text-2xl text-center md:text-left text-black font-black leading-none">
              {promoHeading}
            </h2>
            <p className="mt-2 text-center md:text-left text-gray-500 font-medium">
              {promoSubheading}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3">
            <CourseCard
              course={course}
              useFullTitle
              trackerEvent={() => tracker.skillClickCourse({skill: skillName, course: course.slug})}
              urlParams={{src: 'coaching-skill'}}
            />
          </div>
        </div>
      )}

      {/* About */}
      {faq.length > 0 && (
        <div className="flex flex-col w-full max-w-6xl mx-auto p-4 mb-16">
          <div className="mb-5">
            <h2 className="text-lg md:text-2xl text-center md:text-left text-black font-black leading-none">
              {`About ${skillName}`}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {faq.map((item) => (
              <div key={item.question} className="flex flex-col justify-start">
                <div className="text-base font-semibold">{item.question}</div>
                <p className="mt-2 text-gray-500 font-medium">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default SkillsLandingPage;
